/**
 * Product Navigation Bar
 * Handles sticky navigation and smooth scrolling functionality
 * Uses IntersectionObserver for reliable sticky trigger.
 */

class ProductNavBar {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.staticNavBar = section.querySelector(`#static-nav-${this.sectionId}`);
    this.stickyNavBar = section.querySelector(`#sticky-nav-${this.sectionId}`);
    this.navLinks = section.querySelectorAll('.product-nav-bar__link');
    this.enableSticky = section.dataset.enableSticky === 'true';
    this.observerTrigger = section.querySelector(`#nav-bar-trigger-${this.sectionId}`);
    this.observer = null;

    this.init();
  }

  init() {
    if (!this.staticNavBar || !this.stickyNavBar) return;

    this.staticNavBar.classList.remove('hide');
    this.stickyNavBar.classList.remove('show');

    if (this.enableSticky) {
      this.initStickyWatcher();
    }

    this.navLinks.forEach((link) => {
      link.addEventListener('click', this.handleNavClick.bind(this));
    });

    const ctas = this.section.querySelectorAll('.product-nav-bar__cta [data-add-to-cart="true"]');
    ctas.forEach((btn) => {
      btn.addEventListener('click', this.handleCtaClick.bind(this));
    });

    this.initScrollSpy();
    this.initVariantListener();
  }

  initStickyWatcher() {
    this.ticking = false;
    this.computeStaticTop();
    this.updateStickyState();

    this.onScroll = () => {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.ticking = false;
        this.updateStickyState();
      });
    };

    this.onResize = () => {
      this.computeStaticTop();
      this.updateStickyState();
    };

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onResize);
    window.addEventListener('orientationchange', this.onResize);
    window.addEventListener('load', this.onResize);
  }

  getHeaderHeight() {
    const selectors = ['sticky-header', '.header-wrapper', '#shopify-section-header', 'header'];
    for (const sel of selectors) {
      const el = document.querySelector(sel);
      if (el) return el.offsetHeight || parseInt(getComputedStyle(el).height, 10) || 0;
    }
    return 0;
  }

  computeStaticTop() {
    if (!this.staticNavBar) return;
    const rect = this.staticNavBar.getBoundingClientRect();
    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    this.staticTop = rect.top + scrollY;
    this.staticHeight = this.staticNavBar.offsetHeight || rect.height || 0;
  }

  updateStickyState() {
    const headerHeight = this.getHeaderHeight();
    const currentY = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (this.staticTop == null) this.computeStaticTop();

    // Position sticky nav below header
    this.stickyNavBar.style.top = `${headerHeight}px`;

    // Show sticky when the static nav’s top goes under the header
    const triggerY = (this.staticTop || 0) - headerHeight;

    if (currentY >= triggerY) {
      this.stickyNavBar.classList.add('show');
      this.staticNavBar.classList.add('hide');
    } else {
      this.stickyNavBar.classList.remove('show');
      this.staticNavBar.classList.remove('hide');
    }
  }

  handleNavClick(e) {
    const link = e.target.closest('.product-nav-bar__link');
    if (!link) return;

    const targetSelector = link.getAttribute('href');

    // Skip if it's a void link
    if (!targetSelector || targetSelector === 'javascript:void(0);') {
      e.preventDefault();
      return;
    }

    // Skip if it's an external link or anchor
    if (targetSelector.startsWith('http') || !targetSelector.startsWith('#')) {
      return;
    }

    e.preventDefault();

    // Remove active class from all links
    this.section.querySelectorAll('.product-nav-bar__link').forEach((btn) => {
      btn.classList.remove('is-active');
    });

    // Add active class to clicked link and its counterpart
    const linkText = link.textContent.trim();
    this.section.querySelectorAll('.product-nav-bar__link').forEach((btn) => {
      if (btn.textContent.trim() === linkText) {
        btn.classList.add('is-active');
      }
    });

    // Scroll to target
    this.scrollToTarget(targetSelector);
  }

  scrollToTarget(targetSelector) {
  let targetElement = document.querySelector(targetSelector);

  // Reviews：直接寻找 Judge.me 完整评论区
  if (targetSelector === '#review') {
    targetElement =
      document.querySelector('.jdgm-review-widget') ||
      document.querySelector('.jdgm-rev-widg') ||
      Array.from(document.querySelectorAll('h1, h2, h3, h4')).find((heading) =>
        heading.textContent.trim().toLowerCase().includes('customer reviews')
      );
  }

  if (!targetElement) {
    console.warn(`Scroll target not found: ${targetSelector}`);
    return;
  }

  const headerHeight = this.getHeaderHeight();
  const navBarHeight =
    this.stickyNavBar.offsetHeight ||
    this.staticNavBar.offsetHeight ||
    0;

  const offset = headerHeight + navBarHeight + 20;

  const targetPosition =
    targetElement.getBoundingClientRect().top +
    window.pageYOffset -
    offset;

  window.scrollTo({
    top: targetPosition,
    behavior: 'smooth',
  });
}

  handleCtaClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const mainForm = document.querySelector('product-form.main-product-form form[data-type="add-to-cart-form"]')
      || document.querySelector('form[data-type="add-to-cart-form"]');
    if (!mainForm) return;
    const submitButton = mainForm.querySelector('button[type="submit"]');
    if (submitButton && submitButton.disabled !== true && submitButton.getAttribute('aria-disabled') !== 'true') {
      submitButton.click();
      return;
    }
    if (typeof mainForm.requestSubmit === 'function') {
      mainForm.requestSubmit();
    } else {
      mainForm.dispatchEvent(new Event('submit', { cancelable: true }));
    }
  }

  initScrollSpy() {
    // Get all target sections
    const targetSections = [];
    this.navLinks.forEach((link) => {
      const targetSelector = link.getAttribute('href');
      if (targetSelector && targetSelector.startsWith('#')) {
        const targetElement = document.querySelector(targetSelector);
        if (targetElement) {
          targetSections.push({
            element: targetElement,
            id: targetSelector,
            linkText: link.textContent.trim(),
          });
        }
      }
    });

    if (targetSections.length === 0) return;

    // Observe scroll position and update active state
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const activeSection = targetSections.find((section) => section.element === entry.target);
          if (activeSection) {
            // Remove active class from all links
            this.section.querySelectorAll('.product-nav-bar__link').forEach((link) => {
              link.classList.remove('is-active');
            });

            // Add active class to corresponding links
            this.section.querySelectorAll('.product-nav-bar__link').forEach((link) => {
              if (link.textContent.trim() === activeSection.linkText) {
                link.classList.add('is-active');
              }
            });
          }
        }
      });
    }, observerOptions);

    // Observe all target sections
    targetSections.forEach((section) => {
      observer.observe(section.element);
    });
  }

  initVariantListener() {
    // Listen for variant changes to update button text
    const productForm = document.querySelector('product-form.main-product-form');
    if (!productForm) return;

    // Check for pre-order flag on page load
    this.updateCtaButtonText();

    // Listen for variant change events
    document.addEventListener('variant:change', () => {
      setTimeout(() => {
        this.updateCtaButtonText();
      }, 100);
    });
  }

  updateCtaButtonText() {
    const productForm = document.querySelector('product-form.main-product-form form[data-type="add-to-cart-form"]');
    if (!productForm) return;

    const preorderFlag = productForm.querySelector('.is-preorder-product');
    const navBarPreorderFlag = this.section.querySelector('.nav-bar-preorder-flag');
    
    const ctaLabels = this.section.querySelectorAll('.nav-bar-cta-label');
    const defaultText = this.section.dataset.isPreorder === 'true' ? 
      this.section.dataset.preorderText : 
      ctaLabels[0]?.textContent.trim();

    ctaLabels.forEach((label) => {
      if (preorderFlag || navBarPreorderFlag) {
        const preorderText = preorderFlag?.dataset.preorderText || 
                           navBarPreorderFlag?.dataset.preorderText || 
                           'Pre-order Now';
        label.textContent = preorderText;
      } else if (defaultText && !preorderFlag && !navBarPreorderFlag) {
        // Reset to default if no pre-order
        label.textContent = defaultText;
      }
    });
  }
}

// Initialize on DOMContentLoaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductNavBars);
} else {
  initProductNavBars();
}

function initProductNavBars() {
  const sections = document.querySelectorAll('.product-nav-bar-section');
  sections.forEach((section) => {
    new ProductNavBar(section);
  });
}

// Shopify Section Events
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target.querySelector('.product-nav-bar-section');
    if (section) {
      new ProductNavBar(section);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    // Cleanup if needed
  });

  document.addEventListener('shopify:section:select', (event) => {
    const section = event.target.querySelector('.product-nav-bar-section');
    if (section) {
      // Scroll to the section when selected in theme editor
      section.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });
}